import React from 'react'
import { Typography } from '@material-ui/core'

import useLogs, {
  TUseLogsResult,
} from 'src/parser/entities/Alarm/Preview/Provider/useLogs'
import useUserTasks, {
  TUseUserTasksResult,
} from 'src/parser/entities/Alarm/Preview/Provider/useUserTasks'
import {
  useAlarmAssignedEntities,
  applyGroupNames,
  pendingGroupIds,
  TUseAlarmAssignedEntitiesResult,
} from 'src/parser/entities/Alarm/Preview/Provider/useAlarmAssignedEntity'
import useAssignedGroups from 'src/parser/entities/Alarm/Preview/Provider/useAssignedGroups'
import useAttachments, {
  TUseAttachmentsResult,
} from 'src/parser/entities/Alarm/Preview/Provider/useAttachments'
import useClientLogo, {
  TUseClientLogoResult,
} from 'src/parser/entities/Alarm/Preview/Provider/useClientLogo'
import { IAlarm, IAgentActivityReport } from '@obvious.tech/constellation'
import useAdditionalDetails, {
  TUseAdditionalDetailsResult,
} from 'src/parser/entities/Alarm/Preview/Provider/useAdditionalDetails'
import useAdditionalDetailsCameras, {
  TUseAdditionalDetailsCamerasResult,
} from 'src/parser/entities/Alarm/Preview/Provider/useAdditionalDetailsCameras'
import Loadable, { TLoadableSettled } from 'src/utils/Loadable'
import useGuardTours, {
  TUseGuardToursResults,
} from 'src/parser/entities/AgentActivityReport/ExternalPDF/Provider/useGuardTours'
import { TEntity } from 'src/utils/api'
import { Alert, AlertTitle } from '@material-ui/lab'
import PDFLoading from '../PDFLoading'
import useAgentActivityReportAgent, {
  TUseAgentActivityReportAgentResult,
} from 'src/parser/entities/AgentActivityReport/ExternalPDF/Provider/useAssignedPassport'
import { AlarmMapProvider, AgentActivityMapProvider } from './MapProvider'
import { TNonEmptyArray } from 'src/types'
import { useTranslation } from 'react-i18next'

export type TAgentReportProps = {
  map: any
  loading: boolean
  entity: TEntity<IAgentActivityReport>
  assignedPassport: TLoadableSettled<TUseAgentActivityReportAgentResult>
  guardTours: TLoadableSettled<TUseGuardToursResults>
}

export type TAlarmReportProps = {
  map: any
  loading: boolean
  entity: TEntity<IAlarm>
  additionalDetails: TLoadableSettled<TUseAdditionalDetailsResult>
  cameras: TLoadableSettled<TUseAdditionalDetailsCamerasResult>
  logs: TLoadableSettled<TUseLogsResult>
  tasks: TLoadableSettled<TUseUserTasksResult>
  assignedEntities: TLoadableSettled<TUseAlarmAssignedEntitiesResult>
  attachments: TLoadableSettled<TUseAttachmentsResult>
  clientLogo: TLoadableSettled<TUseClientLogoResult>
}

type TAlarmLoadedProps = React.PropsWithChildren<TAlarmReportProps>
type TAgentLoadedProps = React.PropsWithChildren<TAgentReportProps>

const Loaded = ({
  loading,
  children,
  ...rest
}: TAgentLoadedProps | TAlarmLoadedProps): JSX.Element => {
  return loading ? (
    <PDFLoading />
  ) : (
    /* @ts-expect-error */
    React.cloneElement(children, { ...rest })
  )
}

export type TAgentProps = React.PropsWithChildren<{
  entity: TEntity<IAgentActivityReport>
  authorization?: string
}>

export type TAlarmProps = React.PropsWithChildren<{
  entity: TEntity<IAlarm>
  authorization?: string
}>

const FailedReport = ({
  errors,
}: {
  errors: Array<[string, TNonEmptyArray<NonNullable<unknown>>]>
}): JSX.Element | null => {
  const { t } = useTranslation()

  if (errors.length === 0) return null

  return (
    <Alert severity='error'>
      <AlertTitle>{t('error.pdfCreationFailed')}</AlertTitle>

      {errors.map(([from, errors], i) => {
        return (
          <>
            {errors.map(error => {
              const message =
                'message' in error ? String(error.message) : 'Unexpected error'

              return <Typography>{`${from}: ${message}`}</Typography>
            })}
          </>
        )
      })}
    </Alert>
  )
}

const Provider = ({
  entity,
  authorization,
  loadables,
  children,
  MapProvider,
}): JSX.Element => {
  return (
    <MapProvider
      loading={false}
      entity={entity}
      authorization={authorization}
    >
      <Loaded
        map={undefined}
        entity={entity}
        {...loadables.data}
      >
        {children}
      </Loaded>
    </MapProvider>
  )
}

export const AlarmProvider = ({
  entity,
  authorization,
  children,
}: TAlarmProps): React.ReactElement => {
  const resolvedAssignment = useAlarmAssignedEntities({
    assignedPassports: entity.assignedPassports,
    assignedGroups: entity.assignedGroups,
    /* legacy fallback only — the deprecated `assignedPassport` may contain a group
    see https://gitlab.obvious.tech/obvious/ooda/-/issues/5421 */
    legacyAssignedPassport: entity.assignedPassport,
  })

  const groups = useAssignedGroups({
    ids: pendingGroupIds(resolvedAssignment),
    authorization,
  })

  const alarmLoadables = {
    additionalDetails: useAdditionalDetails({
      alarm: entity,
    }),
    cameras: useAdditionalDetailsCameras({
      alarm: entity,
      authorization,
    }),
    logs: useLogs({
      alarm: entity,
      authorization,
    }),
    tasks: useUserTasks({
      alarm: entity,
      authorization,
    }),
    assignedEntities: applyGroupNames(resolvedAssignment, groups),
    attachments: useAttachments({
      alarm: entity,
      authorization,
    }),
    clientLogo: useClientLogo({
      authorization,
    }),
  }

  const loadables = Loadable.allSettled(alarmLoadables)

  if (!Loadable.settled(loadables)) return <PDFLoading />

  return (
    <Provider
      entity={entity}
      authorization={authorization}
      loadables={loadables}
      MapProvider={AlarmMapProvider}
    >
      {children}
    </Provider>
  )
}

export const AgentActivityProvider = ({
  entity,
  authorization,
  children,
}: TAgentProps): React.ReactElement => {
  const assignedPassport = useAgentActivityReportAgent({
    agentActivityReport: entity,
    authorization,
  })

  const agentReportLoadables = {
    assignedPassport,
    guardTours: useGuardTours({
      entity,
      authorization,
    }),
  }
  if (Loadable.isFailure(assignedPassport)) {
    return (
      <FailedReport errors={[['assignedPassport', [assignedPassport.error]]]} />
    )
  }

  const loadables = Loadable.allSettled(agentReportLoadables)

  if (!Loadable.settled(loadables)) return <PDFLoading />

  if (loadables.loading || !loadables.loaded) return <PDFLoading />

  return (
    <Provider
      entity={entity}
      authorization={authorization}
      loadables={loadables}
      MapProvider={AgentActivityMapProvider}
    >
      {children}
    </Provider>
  )
}
